import { useSyncExternalStore } from 'react'

/** Evento do Chrome/Edge que abre o diálogo nativo de instalação. Não está no lib.dom. */
interface EventoInstalacao extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export type EstadoInstalacao =
  | 'instalado' // já está aberto como app
  | 'disponivel' // o navegador oferece o diálogo nativo
  | 'ios' // Safari: só pelo menu Compartilhar
  | 'manual' // outros navegadores: pelo menu do navegador

let eventoGuardado: EventoInstalacao | null = null
let instalado = false
const ouvintes = new Set<() => void>()
const avisar = () => ouvintes.forEach((ouvinte) => ouvinte())

// O navegador dispara o evento uma vez, logo ao carregar a página: precisa ser
// capturado aqui (importado no entrypoint), antes de a aba Conta existir.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (evento) => {
    evento.preventDefault()
    eventoGuardado = evento as EventoInstalacao
    avisar()
  })
  window.addEventListener('appinstalled', () => {
    eventoGuardado = null
    instalado = true
    avisar()
  })

  // Service worker mínimo (sem cache): só para o navegador considerar o app instalável.
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/service-worker.js').catch(() => {})
  }
}

function rodandoComoApp(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

function ehIos(): boolean {
  const ua = navigator.userAgent
  // iPadOS se apresenta como Mac; o toque denuncia.
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
}

function estadoAtual(): EstadoInstalacao {
  if (instalado || rodandoComoApp()) return 'instalado'
  if (eventoGuardado) return 'disponivel'
  return ehIos() ? 'ios' : 'manual'
}

function assinar(ouvinte: () => void) {
  ouvintes.add(ouvinte)
  return () => ouvintes.delete(ouvinte)
}

/** Abre o diálogo nativo. Retorna false se não houver um disponível. */
async function instalar(): Promise<boolean> {
  const evento = eventoGuardado
  if (!evento) return false
  eventoGuardado = null // o mesmo evento só pode ser usado uma vez
  await evento.prompt()
  const { outcome } = await evento.userChoice
  if (outcome === 'accepted') instalado = true
  avisar()
  return true
}

export function useInstalacao() {
  const estado = useSyncExternalStore(assinar, estadoAtual, () => 'manual' as const)
  return { estado, instalar }
}
